import { useEffect, useState } from "react";
import { differenceInCalendarDays, format, isValid, parseISO, startOfToday } from "date-fns";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, UserCheck, Mail, AlertCircle, ExternalLink, ChevronRight, Search, Star, TrendingUp, Shield, Info } from "lucide-react";

export default function StateInfoHub() {
  const { stateName } = useParams();
  const [electionsData, setElectionsData] = useState(null);
  const [fallbackData, setFallbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("primary");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedElection, setSelectedElection] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    window.scrollTo(0, 0);
    
    fetch('/elections.json')
      .then((res) => res.json())
      .then((data) => {
        setFallbackData(data);
      })
      .catch(() => {
        console.warn('Failed to load fallback elections data');
      });
    
    fetch(`${apiUrl}/elections/state/${stateName}`)
      .then((res) => res.json())
      .then((data) => {
        setElectionsData(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [stateName]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading election information...</p>
        </div>
      </div>
    );
  }

  if (!electionsData?.success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Information Available</h2>
          <p className="text-gray-600 mb-4">We couldn't find election information for {stateName}.</p>
          <Link to="/" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  const elections = Array.isArray(electionsData?.data?.elections) ? electionsData.data.elections : [];
  const filteredElections = elections.filter(election =>
    election.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStateSOSFallback = () => {
    if (!fallbackData || !stateName) return null;
    
    const stateCodeToName = {
      'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
      'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
      'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
      'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
      'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
      'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
      'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
      'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
      'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
      'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
      'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
      'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
      'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'DC'
    };
    
    const fullStateName = stateCodeToName[stateName?.toUpperCase()];
    if (!fullStateName) return null;
    
    const stateData = fallbackData.find(state => 
      state.State.toLowerCase() === fullStateName.toLowerCase()
    );
    return stateData?.["SOS/DataEndpoint"] || null;
  };

  const primaryElections = filteredElections.filter(e => e.electionType === "PRIMARY");
  const generalElections = filteredElections.filter(e => e.electionType === "GENERAL");
  const specialElections = filteredElections.filter(e => e.electionType === "SPECIAL");
  const otherElections = filteredElections.filter(e => e.electionType === "OTHERS");
  const hasOtherElections = elections.some(e => e.electionType === "OTHERS");

  const parseElectionDate = (dateString) => {
    if (!dateString) return null;

    const parsedDate = parseISO(dateString);
    return isValid(parsedDate) ? parsedDate : null;
  };

  const formatDate = (dateString) => {
    const parsedDate = parseElectionDate(dateString);
    console.log(parsedDate)
    return parsedDate ? format(parsedDate, "EEEE, MMMM d, yyyy") : dateString || "TBD";
  };

  const getDaysUntilElection = (dateString) => {
    const parsedDate = parseElectionDate(dateString);
    if (!parsedDate) return null;

    return differenceInCalendarDays(parsedDate, startOfToday());
  };

  const getStatusColor = (election) => {
    if (!election.voterInfoEnriched) return 'bg-gray-100 text-gray-600';
    const daysUntil = getDaysUntilElection(election.electionDay);
    if (daysUntil === null) return 'bg-gray-100 text-gray-600';
    if (daysUntil <= 7) return 'bg-red-100 text-red-700';
    if (daysUntil <= 30) return 'bg-orange-100 text-orange-700';
    return 'bg-green-100 text-green-700';
  };

  const getStatusText = (election) => {
    if (!election.voterInfoEnriched) return 'Limited Info';
    const daysUntil = getDaysUntilElection(election.electionDay);
    if (daysUntil === null) return 'Date TBD';
    if (daysUntil <= 0) return 'Election Day';
    if (daysUntil === 1) return 'Tomorrow';
    if (daysUntil <= 7) return `${daysUntil} days away`;
    if (daysUntil <= 30) return `${daysUntil} days away`;
    return 'Upcoming';
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="bg-gradient-to-bl from-black to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">{stateName?.charAt(0).toUpperCase() + stateName?.slice(1)} Elections</h1>
              <p className="text-blue-100 text-lg">Stay informed about upcoming elections and voting requirements</p>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                <div className="text-sm text-blue-100">Total Elections</div>
                <div className="text-2xl font-bold">{elections.length}</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                <div className="text-sm text-blue-100">Enriched Data</div>
                <div className="text-2xl font-bold">{elections.filter(e => e.voterInfoEnriched).length}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <QuickActionCard
            title="Register to Vote"
            description="Get registered to vote in your state"
            icon={<UserCheck className="w-6 h-6" />}
            color="bg-blue-500"
            url={elections[0]?.officialStateWebsite || elections[0]?.sosEndpoint || getStateSOSFallback()}
          />
          <QuickActionCard
            title="Check Registration"
            description="Verify your voter registration status"
            icon={<Search className="w-6 h-6" />}
            color="bg-green-500"
            url="/check-registration"
          />
          <QuickActionCard
            title="Mail Ballot"
            description="Request an absentee ballot"
            icon={<Mail className="w-6 h-6" />}
            color="bg-purple-500"
            url={elections[0]?.ballotInfoUrl || elections[0]?.officialStateWebsite || getStateSOSFallback()}
          />
          <QuickActionCard
            title="Election Reminders"
            description="Never miss an important deadline"
            icon={<Calendar className="w-6 h-6" />}
            color="bg-orange-500"
            url="/reminder-subscription"
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search elections..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSearchTerm("")}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
              <TabButton
                active={activeTab === "primary"}
                onClick={() => setActiveTab("primary")}
                label="Primary Elections"
                count={primaryElections.length}
                icon={<Calendar className="w-4 h-4" />}
              />
              <TabButton
                active={activeTab === "general"}
                onClick={() => setActiveTab("general")}
                label="General Elections"
                count={generalElections.length}
                icon={<TrendingUp className="w-4 h-4" />}
              />
              <TabButton
                active={activeTab === "special"}
                onClick={() => setActiveTab("special")}
                label="Special Elections"
                count={specialElections.length}
                icon={<Star className="w-4 h-4" />}
              />
            {hasOtherElections && (
              <TabButton
                active={activeTab === "others"}
                onClick={() => setActiveTab("others")}
                label="others"
                count={otherElections.length}
                icon={<Info className="w-4 h-4" />}
              />
            )}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        {(() => {
          const currentElections = activeTab === "primary" ? primaryElections : 
                                 activeTab === "general" ? generalElections : 
                                 activeTab === "special" ? specialElections : 
                                 activeTab === "others" ? otherElections :
                                 filteredElections;
          
          if (currentElections.length === 0) {
            let emptyStateMessage = "";
            let emptyStateDescription = "";
            
            if (activeTab === "primary") {
              emptyStateMessage = "No primary elections available at the moment";
              emptyStateDescription = "Primary elections are typically held to select party nominees";
            } else if (activeTab === "general") {
              emptyStateMessage = "No general elections available at the moment";
              emptyStateDescription = "General elections are where final officeholders are chosen";
            } else if (activeTab === "special") {
              emptyStateMessage = "No special elections available at the moment";
              emptyStateDescription = "Special elections are called as needed for specific purposes";
            } else if (activeTab === "others") {
              emptyStateMessage = "No other elections available at the moment";
              emptyStateDescription = "Other elections will appear here when they are provided for this state";
            } else {
              emptyStateMessage = "No elections available at the moment";
              emptyStateDescription = "Kindly Check back later for future election dates and information";
            }
            
            return (
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{emptyStateMessage}</h3>
                <p className="text-gray-500">{emptyStateDescription}</p>
              </div>
            );
          }
          
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentElections.map((election) => (
                <ElectionCard
                  key={election.id}
                  election={election}
                  onSelect={() => setSelectedElection(election)}
                  formatDate={formatDate}
                  getStatusColor={getStatusColor}
                  getStatusText={getStatusText}
                />
              ))}
            </div>
          );
        })()}
      </div>

      {/* Election Detail Modal */}
      {selectedElection && (
        <ElectionDetailModal
          election={selectedElection}
          onClose={() => setSelectedElection(null)}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

// Helper Components
function QuickActionCard({ title, description, icon, color, url }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
    >
      <div className={`${color} rounded-lg p-3 w-12 h-12 flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
        {icon}
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </a>
  );
}

function TabButton({ active, onClick, label, count, icon }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
        active
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {icon}
      <span>{label}</span>
      {count > 0 && (
        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
          {count}
        </span>
      )}
    </button>
  );
}

function ElectionCard({ election, onSelect, formatDate, getStatusColor, getStatusText }) {
  const getElectionTypeColor = (type) => {
    switch(type) {
      case 'PRIMARY': return 'bg-blue-100 text-blue-700';
      case 'GENERAL': return 'bg-green-100 text-green-700';
      case 'SPECIAL': return 'bg-purple-100 text-purple-700';
      case 'OTHERS': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(election)}`}>
                {getStatusText(election)}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getElectionTypeColor(election.electionType)}`}>
                {election.electionType}
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{election.name}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                {formatDate(election.electionDay)}
              </div>
              <div className="flex items-center text-gray-600">
                <Clock className="w-4 h-4 mr-2" />
                {election.electionHours || 'Not specified'}
              </div>
            </div>
          </div>
        </div>
        
        <button
          onClick={() => onSelect(election)}
          className="w-full mt-4 bg-blue-50 text-blue-600 py-2 px-4 rounded-lg hover:bg-blue-100 transition-colors font-medium text-sm flex items-center justify-center"
        >
          View Details
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
}

function ElectionDetailModal({ election, onClose, formatDate }) {
  const getElectionTypeColor = (type) => {
    switch(type) {
      case 'PRIMARY': return 'bg-blue-100 text-blue-700';
      case 'GENERAL': return 'bg-green-100 text-green-700';
      case 'SPECIAL': return 'bg-purple-100 text-purple-700';
      case 'OTHERS': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{election.name}</h2>
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getElectionTypeColor(election.electionType)}`}>
                  {election.electionType}
                </span>
                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                  {election.stateCode}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoSection
              title="Election Date"
              value={formatDate(election.electionDay)}
              icon={<Calendar className="w-5 h-5" />}
            />
            <InfoSection
              title="Election Hours"
              value={election.electionHours || 'Not specified'}
              icon={<Clock className="w-5 h-5" />}
            />
            <InfoSection
              title="Registration Deadline"
              value={formatDate(election.registrationDeadline)}
              icon={<UserCheck className="w-5 h-5" />}
            />
            <InfoSection
              title="Mail Request Deadline"
              value={formatDate(election.mailRequestDeadline)}
              icon={<Mail className="w-5 h-5" />}
            />
            <InfoSection
              title="Mail Return Deadline"
              value={formatDate(election.mailReturnDeadline)}
              icon={<Mail className="w-5 h-5" />}
            />
            <InfoSection
              title="Early Voting"
              value={election.earlyVotingStartDate && election.earlyVotingEndDate 
                ? `${formatDate(election.earlyVotingStartDate)} - ${formatDate(election.earlyVotingEndDate)}`
                : 'Not available'
              }
              icon={<Calendar className="w-5 h-5" />}
            />
          </div>
          
          <div className="border-t pt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Voting Requirements</h3>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Shield className="w-5 h-5 text-gray-600 mr-2" />
                  <span className="font-medium text-gray-900">Voter ID Requirement</span>
                </div>
                <p className="text-gray-600">{election.voterIdRequirement || 'Not specified'}</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Info className="w-5 h-5 text-gray-600 mr-2" />
                  <span className="font-medium text-gray-900">Felony Voting Rules</span>
                </div>
                <p className="text-gray-600">{election.felonyVotingRules || 'Not specified'}</p>
              </div>
            </div>
          </div>
          
          {election.officialStateWebsite && (
            <div className="border-t pt-6">
              <a
                href={election.officialStateWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                Visit Official State Election Website
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoSection({ title, value, icon }) {
  return (
    <div className="flex items-start space-x-3">
      <div className="bg-blue-100 rounded-lg p-2 text-blue-600">
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-500 mb-1">{title}</div>
        <div className="text-gray-900">{value || 'Not specified'}</div>
      </div>
    </div>
  );
}
